import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, MessageCircle, Sparkles, CheckCircle2 } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";
import { mediaUrl } from "@/lib/api";

const renderHighlight = (title, hi) => {
  if (!hi || !title.includes(hi)) return title;
  const [a, ...rest] = title.split(hi);
  return <>{a}<span className="text-[#0CDBF2]">{hi}</span>{rest.join(hi)}</>;
};

export default function PublicHome() {
  const { content } = useSiteContent();
  const h = content.home || {};

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grid-noise">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0CDBF2]/10 border border-[#0CDBF2]/30 text-[#0CDBF2] text-xs font-mono uppercase tracking-widest" data-testid="hero-badge">
              <Sparkles size={12} /> {h.hero_kicker}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              {renderHighlight(h.hero_title_line1 || "", h.hero_highlight)}<br />
              {h.hero_title_line2}
            </h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
              {h.hero_description}
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 pt-2">
              <Link to="/orcamento" data-testid="hero-cta-quote" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold">
                  {h.hero_cta_primary} <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/produtos" data-testid="hero-cta-products" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-slate-700 hover:bg-slate-800 text-slate-100">
                  {h.hero_cta_secondary}
                </Button>
              </Link>
              {h.hero_cta_whatsapp && (
                <a href={h.hero_whatsapp_url || "#"} target="_blank" rel="noreferrer" data-testid="hero-cta-whatsapp" className="w-full sm:w-auto">
                  <Button size="lg" variant="ghost" className="w-full sm:w-auto text-[#0CDBF2] hover:bg-[#0CDBF2]/10">
                    <MessageCircle size={16} className="mr-2" /> {h.hero_cta_whatsapp}
                  </Button>
                </a>
              )}
            </div>
            {h.stats?.length > 0 && (
              <div className="flex flex-wrap gap-x-6 gap-y-3 pt-6 text-sm">
                {h.stats.map((s, i) => (
                  <div key={i}><div className="font-heading text-xl sm:text-2xl font-bold text-white">{s.value}</div><div className="text-slate-500">{s.label}</div></div>
                ))}
              </div>
            )}
          </div>
          {(h.hero_image_main || h.hero_image_a || h.hero_image_b) && (
            <div className="lg:col-span-5 grid grid-cols-2 gap-3">
              {h.hero_image_main && (
                <div className="col-span-2 rounded-2xl overflow-hidden border border-slate-800 aspect-[16/10]">
                  <img src={mediaUrl(h.hero_image_main)} alt="Uniformes ATP" className="w-full h-full object-cover" />
                </div>
              )}
              {h.hero_image_a && (
                <div className="rounded-2xl overflow-hidden border border-slate-800 aspect-square">
                  <img src={mediaUrl(h.hero_image_a)} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              {h.hero_image_b && (
                <div className="rounded-2xl overflow-hidden border border-slate-800 aspect-square">
                  <img src={mediaUrl(h.hero_image_b)} alt="" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CATEGORIES */}
      {h.categories?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="mb-10">
            <div className="text-xs font-mono uppercase tracking-widest text-[#0CDBF2] mb-3">{h.categories_kicker}</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">{h.categories_title}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {h.categories.map((c, i) => (
              <div key={i} data-testid={`cat-${i}`}
                className="group rounded-2xl overflow-hidden border border-slate-800 bg-[#0B0F17] transition-colors hover:border-[#0CDBF2]/50">
                <div className="aspect-[16/9] overflow-hidden">
                  {c.image && <img src={mediaUrl(c.image)} alt={c.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2 text-[#0CDBF2]"><Sparkles size={16} /><span className="text-xs uppercase tracking-widest font-mono">Categoria</span></div>
                  <div className="font-heading text-xl font-semibold mb-2">{c.title}</div>
                  <p className="text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* DIFERENCIAIS / INFO */}
      {h.info_items?.length > 0 && (
        <section className="border-y border-slate-800/60 bg-[#0B0F17]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-3 gap-8">
            {h.info_items.map((d, i) => (
              <div key={i} className="space-y-2">
                <CheckCircle2 className="text-[#0CDBF2]" />
                <div className="font-heading text-lg font-semibold">{d.title}</div>
                <p className="text-sm text-slate-400 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* GOOGLE REVIEWS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 border-t border-slate-800/60" data-testid="google-reviews">
        <div className="mb-6 text-center">
          <div className="text-xs font-mono uppercase tracking-widest text-[#0CDBF2] mb-2">Avaliações</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">O que dizem no Google</h2>
        </div>
        <div className="review-widget_net" data-uuid="f3be58c6-19f8-4bd3-9953-0d59b1f05f75" data-template="10" data-lang="pt" data-theme="dark"></div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">{h.cta_title}</h2>
        <p className="text-slate-400 mb-8">{h.cta_description}</p>
        <Link to="/orcamento" data-testid="cta-final-quote">
          <Button size="lg" className="bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold">
            {h.cta_button || "Solicitar orçamento agora"} <ArrowRight size={16} className="ml-2" />
          </Button>
        </Link>
      </section>
    </div>
  );
}
